import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import HtmlText from '@/common/components/HtmlText';
import { OralExamFactsSummary } from '@/common/components/OralExamFactsSummary';

export type OralExamDescriptionPreviewProps = {
  resolvedQuestionCount: number | null | undefined;
  resolvedSecondsPerQuestion: number | null | undefined;
  poolSize: number;
  commentsHtml?: string;
};

/**
 * OralExamDescriptionPreview
 * --------------------------
 * Read-only lecturer preview of the student-facing oral exam description:
 * auto-generated facts narrative plus an optional folded-in lecturer note.
 *
 * The label is rendered through FormControl/FormControlLabelText — the same
 * primitives FormField uses — so it aligns visually with the sibling field
 * labels (Title, Type, Additional Comments).
 */
export function OralExamDescriptionPreview({
  resolvedQuestionCount,
  resolvedSecondsPerQuestion,
  poolSize,
  commentsHtml,
}: OralExamDescriptionPreviewProps) {
  const { t } = useTranslation();
  // Strip HTML tags so an empty editor value doesn't falsely trigger the
  // separator or the comment section.
  const hasComment = !!(commentsHtml || '').replace(/<[^>]*>/g, '').trim();

  return (
    <FormControl testID="oral-exam-student-preview">
      <FormControlLabel>
        <HStack alignItems="center" space="sm">
          <FormControlLabelText size="xs">
            {t('lecturer.moduleEditor.taskEditor.description.previewEyebrow')}
          </FormControlLabelText>
          <Box
            bg="$statusInfoSubtle"
            px="$2"
            py="$0.5"
            borderRadius="$sm"
            testID="oral-exam-preview-autogenerated-badge"
          >
            <Text size="2xs" color="$textOnInfoSubtle" fontWeight="semibold">
              {t('lecturer.moduleEditor.taskEditor.description.autoGeneratedBadge')}
            </Text>
          </Box>
        </HStack>
      </FormControlLabel>

      <Box bg="$bgSubtle" borderRadius="$md" p="$4">
        <VStack space="sm">
          <OralExamFactsSummary
            resolvedQuestionCount={resolvedQuestionCount}
            resolvedSecondsPerQuestion={resolvedSecondsPerQuestion}
            poolSize={poolSize}
          />
          {hasComment && (
            <Box
              borderTopWidth={1}
              borderColor="$borderLight"
              pt="$3"
              mt="$1"
            >
              <HtmlText
                content={commentsHtml}
                fontSize={13}
                testID="oral-exam-student-preview-description"
              />
            </Box>
          )}
        </VStack>
      </Box>
    </FormControl>
  );
}
